import { forwardRef, type ButtonHTMLAttributes } from 'react'

type Variant = 'primary' | 'secondary' | 'ghost' | 'danger'

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: Variant
}

const variants: Record<Variant, string> = {
  // Amber CTA with dark ink text and a soft amber glow — the brand's
  // "amber is earned" moment. Hover brightens (dark-theme equivalent of
  // darkening on light).
  primary:
    'bg-glow-600 text-ink hover:bg-glow-700 shadow-[0_0_24px_rgba(232,169,74,0.18)] hover:shadow-[0_0_32px_rgba(232,169,74,0.32)] disabled:bg-glow-600/50 disabled:shadow-none disabled:cursor-not-allowed',
  secondary:
    'border border-line-strong bg-surface text-cream hover:bg-surface-2 disabled:opacity-50 disabled:cursor-not-allowed',
  ghost:
    'text-muted hover:bg-surface-2 hover:text-cream disabled:opacity-50 disabled:cursor-not-allowed',
  danger:
    'bg-red-600 text-white hover:bg-red-500 disabled:bg-red-600/50 disabled:cursor-not-allowed',
}

// buttonClasses builds the shared button styling for cases where a real
// <button> is wrong — e.g. an <a> that needs to look like a button
// (opening a draft preview in a new tab). Keeps anchor + button visually
// in sync without duplicating the class string.
export function buttonClasses(variant: Variant = 'primary', className = ''): string {
  return `inline-flex items-center justify-center gap-2 rounded-md px-4 py-2 text-sm font-medium transition-colors ${variants[variant]} ${className}`
}

// Button — variants share padding/radius/font weight; only colour scheme
// changes per variant. forwardRef so dialogs and focus management can
// target it directly.
export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  ({ variant = 'primary', className = '', ...rest }, ref) => (
    <button
      ref={ref}
      type={rest.type ?? 'button'}
      {...rest}
      className={buttonClasses(variant, className)}
    />
  ),
)
Button.displayName = 'Button'
